<script setup lang="ts">
import { computed } from 'vue'
import { useSnackbarQueue, type SnackbarVariant } from '@/composables/useSnackbarQueue'
import { useReducedMotion } from '@/composables/useReducedMotion'

const { current, dismiss } = useSnackbarQueue()
const isReduced = useReducedMotion()

const transitionName = computed(() => (isReduced.value ? 'fb-toast-fade' : 'fb-toast'))

const variantMap: Record<SnackbarVariant, { color: string; icon: string }> = {
  approve: { color: '#FFC83D', icon: 'mdi-star' },
  sendBack: { color: '#FFB3BA', icon: 'mdi-undo' },
  saved: { color: '#3FD8A0', icon: 'mdi-check' },
  restore: { color: '#3FD8A0', icon: 'mdi-check' },
  info: { color: '#B79CFF', icon: 'mdi-information-outline' },
  blocked: { color: '#B79CFF', icon: 'mdi-lock-outline' },
}

const currentVariant = computed<SnackbarVariant>(() => current.value?.variant ?? 'info')
const badge = computed(() => variantMap[currentVariant.value])

function handleAction() {
  current.value?.action?.handler?.()
  dismiss()
}

function onUpdateModelValue(val: boolean) {
  if (!val) {
    dismiss()
  }
}
</script>

<template>
  <v-snackbar
    :key="current?.id"
    :model-value="!!current"
    :timeout="current?.timeout ?? 5000"
    :transition="transitionName"
    :data-testid="current?.testId || 'fb-snackbar'"
    role="status"
    aria-live="polite"
    @update:model-value="onUpdateModelValue"
  >
    <div class="fb-snackbar-content">
      <v-avatar
        size="32"
        :color="badge.color"
        data-testid="fb-snackbar-icon"
        :data-variant="currentVariant"
      >
        <v-icon :icon="badge.icon" size="18" color="#1E1535" aria-hidden="true" />
      </v-avatar>
      <span class="fb-snackbar-text">{{ current?.text }}</span>
    </div>

    <template v-if="current?.action" #actions>
      <v-btn
        variant="text"
        data-testid="fb-snackbar-action"
        class="fb-snackbar-action font-weight-bold"
        color="secondary"
        @click="handleAction"
      >
        {{ current.action.label }}
      </v-btn>
    </template>
  </v-snackbar>
</template>

<style scoped>
.fb-snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fb-snackbar-text {
  font-size: 15px;
  font-weight: 700;
}

.fb-snackbar-action {
  min-height: 44px;
  min-width: 44px;
  color: rgb(var(--v-theme-secondary));
}
</style>
